/**
 * Authentication API
 */

import { apiClient } from "./client";
import type {
  APIError,
  LoginResponse,
  LoginResponseWith2FA,
  TwoFAEnableResponse,
  TwoFADisableResponse,
  TwoFAResendResponse,
  TwoFAStatusResponse,
  User,
} from "./types";

export interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  token_type: string;
}

export interface LoginResult {
  requires2FA: boolean;
  partialToken?: string;
  user?: User;
}

/**
 * Login with username and password
 * Returns either full auth or requires 2FA verification
 */
export async function login(
  username: string,
  password: string
): Promise<LoginResult> {
  // Use JSON endpoint for 2FA support
  const response = await apiClient.post<LoginResponseWith2FA>(
    "/api/v1/auth/login/json",
    { username, password },
    false // No auth required
  );

  if (response.requires_2fa) {
    // 2FA required - return partial token
    return {
      requires2FA: true,
      partialToken: response.partial_token,
    };
  }

  // No 2FA - store tokens and get user
  apiClient.setToken(response.access_token!);
  if (response.refresh_token) {
    apiClient.setRefreshToken(response.refresh_token);
  }
  // Don't auto-redirect on 401 - let the caller handle errors
  const user = await getCurrentUser(false);

  return {
    requires2FA: false,
    user,
  };
}

/**
 * Verify 2FA with the 6-digit email code and complete login
 */
export async function verify2FA(
  partialToken: string,
  code: string
): Promise<User> {
  const response = await apiClient.post<TokenResponse>(
    "/api/v1/auth/2fa/verify",
    { partial_token: partialToken, code },
    false // No auth required
  );

  apiClient.setToken(response.access_token);
  if (response.refresh_token) {
    apiClient.setRefreshToken(response.refresh_token);
  }

  // Don't auto-redirect on 401 here - let the caller handle any errors
  // This prevents a race condition where getCurrentUser fails and triggers
  // a hard redirect before the auth context can update its state
  return getCurrentUser(false);
}

/**
 * Resend a fresh 6-digit verification code to the user's email.
 * Returns a new partial_token — the caller MUST swap it in sessionStorage
 * so the verification window stays aligned with the newly-issued OTP expiry.
 */
export async function resend2FACode(
  partialToken: string
): Promise<TwoFAResendResponse> {
  return apiClient.post<TwoFAResendResponse>(
    "/api/v1/auth/2fa/resend",
    { partial_token: partialToken },
    false // No auth required
  );
}

/**
 * Enable email-based 2FA for the current user.
 * Future logins will require a 6-digit code sent to the user's email.
 */
export async function enable2FA(): Promise<TwoFAEnableResponse> {
  return apiClient.post<TwoFAEnableResponse>("/api/v1/auth/2fa/enable", {});
}

/**
 * Disable 2FA for current user
 */
export async function disable2FA(): Promise<TwoFADisableResponse> {
  return apiClient.post<TwoFADisableResponse>("/api/v1/auth/2fa/disable", {});
}

/**
 * Get 2FA status for current user
 */
export async function get2FAStatus(): Promise<boolean> {
  const response = await apiClient.get<TwoFAStatusResponse>(
    "/api/v1/auth/2fa/status"
  );
  return response.twofa_enabled;
}

/**
 * Accept Terms of Use for current user.
 *
 * @deprecated Use {@link acceptLegalDocuments} instead — issue #143 added the
 * Data Protection Notice as a sibling document; the combined endpoint flips
 * both flags atomically.
 */
export async function acceptToF(): Promise<User> {
  return apiClient.post<User>("/api/v1/auth/accept-tof", {});
}

/**
 * Accept Terms of Use AND Data Protection Notice together (issue #143).
 *
 * Issued by the first-login acceptance dialog. Atomically sets both
 * ``has_accepted_tof`` and ``has_accepted_dpn`` on the current user.
 */
export async function acceptLegalDocuments(): Promise<User> {
  return apiClient.post<User>("/api/v1/auth/accept-legal", {});
}

/**
 * Legacy login function for backward compatibility.
 * Uses form data (OAuth2 flow). Since the backend added 2FA support, this
 * endpoint returns 400 for any user with 2FA enabled — the caller will get
 * an APIError they can surface. Prefer `login()` for normal flows.
 */
export async function loginLegacy(
  username: string,
  password: string
): Promise<LoginResponse> {
  const formData = new URLSearchParams();
  formData.append("username", username);
  formData.append("password", password);

  const tokenResponse = await apiClient.postForm<TokenResponse>(
    "/api/v1/auth/login",
    formData
  );

  apiClient.setToken(tokenResponse.access_token);
  if (tokenResponse.refresh_token) {
    apiClient.setRefreshToken(tokenResponse.refresh_token);
  }
  const user = await getCurrentUser();

  return {
    access_token: tokenResponse.access_token,
    token_type: tokenResponse.token_type,
    user,
  };
}

/**
 * Logout - clear token
 */
export function logout(): void {
  apiClient.clearToken();
  if (typeof window !== "undefined") {
    window.location.href = "/auth";
  }
}

/**
 * Get current authenticated user
 * @param autoRedirectOn401 - Whether to redirect to /auth on 401 (default: true)
 */
export async function getCurrentUser(autoRedirectOn401: boolean = true): Promise<User> {
  return apiClient.get<User>("/api/v1/auth/me", true, autoRedirectOn401);
}

/**
 * Refresh the access token using the stored refresh token
 */
export async function refreshToken(): Promise<TokenResponse> {
  const storedRefreshToken = apiClient.getRefreshToken();
  if (!storedRefreshToken) {
    throw { detail: "No refresh token available", status_code: 401 } as APIError;
  }

  const response = await apiClient.post<TokenResponse>(
    "/api/v1/auth/refresh",
    { refresh_token: storedRefreshToken },
    false // No auth header needed for refresh
  );

  if (response.access_token) {
    apiClient.setToken(response.access_token);
  }
  if (response.refresh_token) {
    apiClient.setRefreshToken(response.refresh_token);
  }

  return response;
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return apiClient.isAuthenticated();
}
